import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  getAllFeeTypes,
  createFeeType,
  updateFeeType,
  deleteFeeType,
  getClassLevels
} from "../../../services/feeTypeService";
import { FeeType, FeeTypeCreatePayload, FeeTypeUpdatePayload, PaginatedFeeTypes } from "../../../types/feeTypeTypes";

export const useFeeTypes = () => {
  const { t } = useTranslation();
    const [feeTypes, setFeeTypes] = useState<PaginatedFeeTypes>({
      data: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10000
      }
    });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedFeeType, setSelectedFeeType] = useState<FeeType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [classLevels, setClassLevels] = useState<any[]>([]);

  const fetchFeeTypes = useCallback(async (filters: { [key: string]: string | number | null } = {}) => {
    setLoading(true);
    try {
      const response = await getAllFeeTypes(filters);
      setFeeTypes(response.data);
      setError(null);
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_fetch_fee_types"));
      setError(t("failed_to_fetch_fee_types"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCreate = async (payload: FeeTypeCreatePayload) => {
    try {
      await createFeeType(payload);
      message.success(t("fee_type_added_successfully"));
      await fetchFeeTypes();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_create_fee_type"));
      return false;
    }
  };

  const handleUpdate = async (feeTypeId: string, payload: FeeTypeUpdatePayload) => {
    try {
      await updateFeeType(feeTypeId, payload);
      message.success(t("fee_type_updated_successfully"));
      await fetchFeeTypes();
      return true;
    } catch (err) {
      console.error(err);
      message.error(t("failed_to_update_fee_type"));
      return false;
    }
  };

  const handleDelete = async (feeTypeId: string) => {
    try {
      await deleteFeeType(feeTypeId);
      message.success(t("fee_type_deleted_successfully"));
      await fetchFeeTypes();
      return true;
    } catch (err) {
      console.error(err);
      // message.error(t("failed_to_delete_fee_type"));
      return false;
    }
  };

  const handleGetClassLevels = async () => {
    try {
      const response = await getClassLevels();
      setClassLevels(response.data);
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  return {
    feeTypes: feeTypes.data,
    infoPagination: {
      total: feeTypes.pagination?.total || 0,
      page: feeTypes.pagination?.page || 1,
      limit: feeTypes.pagination?.limit || 10000,
    },
    loading,
    error,
    selectedFeeType,
    setSelectedFeeType,
    isModalOpen,
    setIsModalOpen,
    fetchFeeTypes,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleGetClassLevels,
    classLevels
  };
};

export default useFeeTypes;
